import habitat from 'preact-habitat'
import App from './app'
import version from '../version'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import localeData from 'dayjs/plugin/localeData'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import weekday from 'dayjs/plugin/weekday'
// import preParsePostFormat from 'dayjs/plugin/preParsePostFormat'

// locales
import 'dayjs/locale/en'
import enGb from 'dayjs/locale/en-gb'
import 'dayjs/locale/en-au'
import 'dayjs/locale/fr'
import 'dayjs/locale/pl'
import 'dayjs/locale/it'
import 'dayjs/locale/de'
import 'dayjs/locale/zh-cn'
import 'dayjs/locale/zh-tw'
import 'dayjs/locale/zh-hk'
import 'dayjs/locale/en-sg'
import 'dayjs/locale/pt'
import 'dayjs/locale/sv'
import 'dayjs/locale/nl'
import ar from 'dayjs/locale/ar'
import 'dayjs/locale/br'
import 'dayjs/locale/es'
import 'dayjs/locale/es-mx'
import 'dayjs/locale/ru'
import 'dayjs/locale/ja'
import 'dayjs/locale/ko'
import 'dayjs/locale/da'
import 'dayjs/locale/th'
import 'dayjs/locale/fi'
import 'dayjs/locale/cs'
import 'dayjs/locale/bg'
import 'dayjs/locale/hr'
import 'dayjs/locale/el'
import 'dayjs/locale/hu'
import 'dayjs/locale/ro'
import 'dayjs/locale/sk'
import 'dayjs/locale/sl'
import 'dayjs/locale/tr'
import 'dayjs/locale/uk'
import 'dayjs/locale/af'
import 'dayjs/locale/de-ch'
import 'dayjs/locale/it-ch'
import 'dayjs/locale/fr-ch'

const localesMap = {
  af: 'af',
  ar: 'ar',
  bg: 'bg',
  ch_de: 'de-ch',
  ch_it: 'it-ch',
  ch_fr: 'fr-ch',
  cs: 'cs',
  da: 'da',
  de: 'de',
  el: 'el',
  en: 'en-gb',
  en_au: 'en-au',
  en_ca: 'en-ca',
  en_gb: 'en-gb',
  en_sg: 'en-sg',
  en_us: 'en',
  es: 'es',
  fi: 'fi',
  fr: 'fr',
  fr_be: 'fr-be',
  fr_ca: 'fr-ca',
  hr: 'hr',
  hu: 'hu',
  it: 'it',
  ja: 'ja',
  ko: 'ko',
  nl: 'nl',
  pl: 'pl',
  pt: 'pt-pt',
  ro: 'ro',
  ru: 'ru',
  sc: 'zh-cn',
  sg_sc: 'zh-cn', // Not available in Day.js, we fallback to cn
  sk: 'sk',
  sl: 'sl',
  sv: 'sv',
  th: 'th',
  tr: 'tr',
  uk: 'uk',
  zh: 'zh-cn',
  zh_cn: 'zh-cn',
  zh_hk: 'zh-hk',
  zh_mo: 'zh-cn', // Not available in Day.js, we fallback to cn
  zh_tw: 'zh-tw'
}

const setCSSVariables = theme => {
  const el = document.querySelector(':root')
  Object.keys(theme.colors).forEach(color => {
    el.style.setProperty('--rcc-colors-' + color, theme.colors[color])
  })
  el.style.setProperty('--rcc-font-family', theme.fontFamily)
}

const configureLocale = (sanitizedLocale, sanitizedCountry) => {
  dayjs.extend(localizedFormat)
  // dayjs.extend(preParsePostFormat)
  dayjs.extend(customParseFormat)
  dayjs.extend(localeData)
  dayjs.extend(advancedFormat)
  dayjs.extend(weekday)
  // force en-gb tu use
  enGb.formats.LT = 'h:mm A'
  ar.weekStart = 0
  ar.weekdays = 'الأحد_الاثنين_الثلاثاء_الأربعاء_الخميس_الجمعة_السبت'.split('_')
  ar.weekdaysMin = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
  dayjs.locale(enGb, null, true)
  dayjs.locale(ar, null, true)
  dayjs.locale(localesMap[`${sanitizedLocale}_${sanitizedCountry}`] || localesMap[sanitizedLocale] || 'en')
}

const defaultTheme = {
  colors: {
    primary: '#F5F5F5',
    white: '#fff',
    text: '#222',
    danger: '#840b15',
    gray: '#757575',
    'gray-light': '#ccc',
    'gray-very-light': '#e6e6e6'
  },
  fontFamily:
    'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"'
}
setCSSVariables(defaultTheme)

const widget = {
  version,
  render: ({
    selector,
    maisonId,
    apiBaseUrl,
    country = '',
    boutiqueId,
    recaptchaKey,
    homeLink,
    locale = '',
    theme = defaultTheme,
    useAnalytics,
    analyticsDriver,
    tcId,
    analyticsCountry,
    analyticsLocale,
    internalComments,
    showPrintIcon = false,
    direction = 'ltr',
    povESFY = false,
    povNGCS = false,
    hasStrap = false,
    isPickUp = false,
    isClickNCollect = false,
    onSectionExpanded = () => {} // noop
  }) => {
    const sanitizedLocale = locale.toLowerCase()
    const sanitizedCountry = country.toLowerCase()
    // remove API version
    const sanitizedApiBaseUrl = apiBaseUrl.substring(0, apiBaseUrl.length - 3)

    setCSSVariables(theme)
    configureLocale(sanitizedLocale, sanitizedCountry)
    const { render } = habitat(App)
    return render({
      selector,
      inline: false,
      clean: false,
      clientSpecified: false,
      defaultProps: {
        selector,
        maisonId,
        apiBaseUrl: sanitizedApiBaseUrl,
        country,
        boutiqueId,
        recaptchaKey,
        homeLink,
        locale: sanitizedLocale,
        useAnalytics,
        analyticsDriver,
        tcId,
        analyticsCountry,
        analyticsLocale,
        showPrintIcon,
        direction,
        povESFY,
        povNGCS,
        hasStrap,
        isPickUp,
        isClickNCollect,
        internalComments,
        colors: theme.colors,
        onSectionExpanded
      }
    })
  }
}
export default widget
